import {
  readLayout,
  restoreLayout,
  saveLayout,
  writeLayout
} from './panels/storage.js';
import {
  enterFocus,
  exitFocus,
  toggleFocus
} from './panels/focus.js';
import {
  createResizeHandles,
  handleResize,
  onResizePointerDown
} from './panels/resize.js';
import {
  clampPanelPosition,
  onPointerDown,
  onPointerMove,
  onPointerUp
} from './panels/drag.js';
import {
  minimizeAll,
  resetAll,
  resetPanel,
  restoreAll,
  toggleMinimize
} from './panels/controls.js';
import { isMobileLayout } from '../core/responsive-mode.js';

/**
 * Front-my-End Panel System
 *
 * Public facade for panel persistence, drag/resize, minimize, and Focus Mode.
 * Implementation details live in focused modules under `ui/panels/`.
 */
export class PanelSystem {
  constructor() {
    this.layoutStorageKey = 'front_my_end_panel_layout';
    this.dragging = null;
    this.resizing = null;
    this.startX = 0;
    this.startY = 0;
    this.panelStartX = 0;
    this.panelStartY = 0;
    this.currentTranslateX = 0;
    this.currentTranslateY = 0;
    this.workspace = null;
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.workspace = document.getElementById('workspace');
    if (!this.workspace) return;

    const panels = document.querySelectorAll('.panel');
    panels.forEach(panel => {
      this.createResizeHandles(panel);
      panel.addEventListener('pointerdown', () => {
        this.bringToFront(panel);
      }, { capture: true });
    });

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const panel = entry.target;
        if (panel.matches(':active')) {
          this.bringToFront(panel);
        }
      }
    });
    panels.forEach(panel => {
      resizeObserver.observe(panel);
    });

    const headers = document.querySelectorAll('.panel-header');
    headers.forEach(header => {
      header.addEventListener('pointerdown', this.onPointerDown.bind(this));
    });

    document.addEventListener('pointermove', this.onPointerMove.bind(this));
    document.addEventListener('pointerup', this.onPointerUp.bind(this));
    document.addEventListener('pointercancel', this.onPointerUp.bind(this));

    const minimizeBtns = document.querySelectorAll('.minimize-btn');
    minimizeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const panel = btn.closest('.panel');
        if (panel) this.toggleMinimize(panel);
      });
      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      });
    });

    const focusBtns = document.querySelectorAll('.focus-btn');
    focusBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const panel = btn.closest('.panel');
        if (panel) this.toggleFocus(panel);
      });
      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      });
    });

    headers.forEach(header => {
      header.addEventListener('dblclick', (e) => {
        if (e.target.closest('button, select')) return;
        const panel = header.closest('.panel');
        if (panel) this.resetPanel(panel);
      });
    });

    this.restoreLayout();

    console.log('[PanelSystem] Initialized. Panels are draggable, resizable, focusable, and support Focus Mode.');
  }

  readLayout() {
    return readLayout(this);
  }

  writeLayout(layout) {
    writeLayout(this, layout);
  }

  restoreLayout() {
    restoreLayout(this);
  }

  saveLayout() {
    saveLayout(this);
  }

  createResizeHandles(panel) {
    createResizeHandles(this, panel);
  }

  bringToFront(panel) {
    const allPanels = document.querySelectorAll('.panel');
    allPanels.forEach(p => {
      p.style.zIndex = '10';
    });
    panel.style.zIndex = '100';
  }

  isMobileLayout() {
    return isMobileLayout();
  }

  toggleFocus(panel) {
    toggleFocus(this, panel);
  }

  enterFocus(panel) {
    enterFocus(this, panel);
  }

  exitFocus() {
    exitFocus(this);
  }

  resetPanel(panel) {
    resetPanel(this, panel);
  }

  toggleMinimize(panel) {
    toggleMinimize(this, panel);
  }

  minimizeAll() {
    minimizeAll(this);
  }

  restoreAll() {
    restoreAll(this);
  }

  clampPanelPosition(panel) {
    clampPanelPosition(this, panel);
  }

  isMinimized(panel) {
    return panel.classList.contains('minimized');
  }

  onResizePointerDown(e) {
    onResizePointerDown(this, e);
  }

  handleResize(e) {
    handleResize(this, e);
  }

  onPointerDown(e) {
    onPointerDown(this, e);
  }

  onPointerMove(e) {
    onPointerMove(this, e);
  }

  onPointerUp(e) {
    onPointerUp(this, e);
  }

  resetAll() {
    resetAll(this);
  }
}

export const panelSystem = new PanelSystem();

// Dev/debug escape hatch; app modules receive this from the composition root.
window.__panelSystem = panelSystem;
